import { useEffect, useState } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

// Eagerly import every .png in the screenshots folder at build time.
// Vite resolves spaces in filenames correctly with glob imports.
const modules = import.meta.glob<{ default: string }>(
  '../../assets/screenshots/*.png',
  { eager: true }
)

function labelFromPath(path: string): string {
  const filename = path.split('/').pop() ?? ''
  return filename
    .replace(/^bunker\.ai\s+/i, '')
    .replace(/\.(png|jpg|jpeg)$/i, '')
}

const SCREENSHOTS = Object.entries(modules)
  .sort(([a], [b]) => {
    const aIsStart = /startpage/i.test(a)
    const bIsStart = /startpage/i.test(b)
    if (aIsStart && !bIsStart) return -1
    if (!aIsStart && bIsStart) return 1
    return a.localeCompare(b)
  })
  .map(([path, mod]) => ({ url: mod.default, label: labelFromPath(path) }))

interface ScreenshotGalleryModalProps {
  open: boolean
  onClose: () => void
}

export function ScreenshotGalleryModal({ open, onClose }: ScreenshotGalleryModalProps) {
  const [current, setCurrent] = useState(0)

  const prev = () => setCurrent((c) => (c - 1 + SCREENSHOTS.length) % SCREENSHOTS.length)
  const next = () => setCurrent((c) => (c + 1) % SCREENSHOTS.length)

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Reset to first slide each time the modal opens.
  useEffect(() => {
    if (open) setCurrent(0)
  }, [open])

  if (!open || SCREENSHOTS.length === 0) return null

  const shot = SCREENSHOTS[current]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/88 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-[92vw] xl:max-w-7xl flex-col gap-4 px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          className="absolute -top-12 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white transition-colors"
          onClick={onClose}
          aria-label="Close gallery"
        >
          <X size={20} />
        </button>

        {/* Main image */}
        <div
          className="relative overflow-hidden rounded-2xl bg-slate-950 shadow-2xl"
          style={{ aspectRatio: '16/9' }}
        >
          <img
            key={shot.url}
            src={shot.url}
            alt={shot.label}
            className="h-full w-full object-contain"
          />

          {/* Prev */}
          <button
            type="button"
            className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors"
            onClick={prev}
            aria-label="Previous screenshot"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Next */}
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors"
            onClick={next}
            aria-label="Next screenshot"
          >
            <ChevronRight size={20} />
          </button>

          {/* Caption */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-6 py-5">
            <p className="capitalize font-semibold text-white">{shot.label}</p>
            <p className="text-sm text-white/55">{current + 1} / {SCREENSHOTS.length}</p>
          </div>
        </div>

        {/* Thumbnail strip */}
        <div className="flex justify-center gap-2 overflow-x-auto pb-1">
          {SCREENSHOTS.map((s, i) => (
            <button
              key={s.url}
              type="button"
              onClick={() => setCurrent(i)}
              aria-label={s.label}
              className={[
                'h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-all',
                i === current
                  ? 'border-primary opacity-100 scale-105'
                  : 'border-transparent opacity-50 hover:opacity-80',
              ].join(' ')}
            >
              <img src={s.url} alt={s.label} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
